import { SubitemsService } from "./SubitemsService";

// Status progression order - MUST match the actual order in ProjectStatusManager.js
const STATUS_PROGRESSION = [
  "Startup",
  "Data Prep",
  "Utilities",
  "Ready to Model",
  "Modeling",
  "Double Check",
  "Corrections",
  "Review",
  "Ready to Export",
  "Completed",
];

// Map status to phase for subitems validation
const STATUS_TO_PHASE_MAP = {
  "Data Prep": "01-Data Prep",
  "Utilities": "02-Utilities",
  "Ready to Model": "04-Erosion/Pond", // Ready to Model comes after Erosion/Pond phase completion
  Modeling: "05-Modeling",
  "Double Check": "06-Double Check",
  Corrections: "07-Corrections",
  Review: "08-Review",
  "Ready to Export": "09-Ready to Export",
};

// Phase progression order (04-Erosion/Pond phase kept for subitems)
const PHASE_ORDER = [
  "01-Data Prep",
  "02-Utilities",
  "03-Discrepancy Check",
  "04-Erosion/Pond",
  "05-Modeling",
  "06-Double Check",
  "07-Corrections",
  "08-Review",
  "09-Ready to Export",
];

export const StatusValidationService = {
  /**
   * Check if a status change is valid and return validation result
   * @param {number} projectId - Project ID
   * @param {string} currentStatus - Current modeling status
   * @param {string} targetStatus - Target modeling status
   * @returns {Promise<{isValid: boolean, incompleteSubitems: Array, reason?: string}>}
   */
  async validateStatusChange(projectId, currentStatus, targetStatus) {
    try {
      // Allow backward status changes or no actual change
      const isAdvancement = this.isStatusAdvancement(
        currentStatus,
        targetStatus,
      );
      if (!isAdvancement) {
        return { isValid: true, incompleteSubitems: [] };
      }

      // Get phases that need to be completed before the target status
      const requiredPhases = this.getRequiredPhasesForStatus(targetStatus, currentStatus);
      console.log("🔍 Required phases for", targetStatus, "(excluding current phase):", requiredPhases);

      if (requiredPhases.length === 0) {
        return { isValid: true, incompleteSubitems: [] };
      }

      // Get all subitems for this project
      const allSubitems = await SubitemsService.getSubitemsByProject(projectId);
      if (!allSubitems || allSubitems.length === 0) {
        return { isValid: true, incompleteSubitems: [] };
      }

      // Find incomplete subitems in required phases
      const incompleteSubitems = this.findIncompleteSubitemsInPhases(
        allSubitems,
        requiredPhases,
      );

      console.log(
        "🔍 Found",
        incompleteSubitems.length,
        "incomplete subitems in required phases:",
        incompleteSubitems.map((s) => ({
          name: s.name,
          phase: s.phase,
          status: s.subitemStatus,
        })),
      );

      const result = {
        isValid: incompleteSubitems.length === 0,
        incompleteSubitems,
      };
      return result;
    } catch (error) {
      console.error("Error validating status change:", error);
      // On error, allow the change but log the issue
      return {
        isValid: true,
        incompleteSubitems: [],
        reason: `Validation error: ${error.message}`,
      };
    }
  },

  /**
   * Check if the status change represents an advancement (moving forward)
   * @param {string} currentStatus - Current status
   * @param {string} targetStatus - Target status
   * @returns {boolean}
   */
  isStatusAdvancement(currentStatus, targetStatus) {
    const currentIndex = STATUS_PROGRESSION.indexOf(currentStatus);
    const targetIndex = STATUS_PROGRESSION.indexOf(targetStatus);

    // If either status is not found, assume it's not an advancement
    if (currentIndex === -1 || targetIndex === -1) {
      return false;
    }

    return targetIndex > currentIndex;
  },

  /**
   * Get phases that must be completed before advancing to target status
   * @param {string} targetStatus - Target status
   * @param {string} currentStatus - Current status (to exclude current phase)
   * @returns {Array<string>} Array of phase names
   */
  getRequiredPhasesForStatus(targetStatus, currentStatus = null) {
    const targetPhase = STATUS_TO_PHASE_MAP[targetStatus];

    if (!targetPhase) {
      return [];
    }

    const targetPhaseIndex = PHASE_ORDER.indexOf(targetPhase);

    if (targetPhaseIndex === -1) {
      return [];
    }

    // Get all phases before the target phase
    let requiredPhases = PHASE_ORDER.slice(0, targetPhaseIndex);
    
    // If we have a current status, exclude its phase from required phases
    // This prevents the catch-22 of requiring completion of the current phase to leave it
    if (currentStatus) {
      const currentPhase = STATUS_TO_PHASE_MAP[currentStatus];
      if (currentPhase) {
        requiredPhases = requiredPhases.filter(phase => phase !== currentPhase);
      }
    }

    return requiredPhases;
  },

  /**
   * Find subitems that are incomplete in the specified phases
   * @param {Array} subitems - All subitems for the project
   * @param {Array<string>} phases - Phases to check
   * @returns {Array} Incomplete subitems
   */
  findIncompleteSubitemsInPhases(subitems, phases) {
    return subitems.filter((subitem) => {
      // Check if subitem belongs to one of the required phases
      if (!phases.includes(subitem.phase)) {
        return false;
      }

      // Consider incomplete if: no status, null, 'Working on it', or 'Stuck'
      const status = subitem.subitemStatus;
      return (
        !status ||
        status === null ||
        status === "Working on it" ||
        status === "Stuck"
      );
    });
  },

  /**
   * Get human-readable status progression description
   * @param {string} currentStatus - Current status
   * @param {string} targetStatus - Target status
   * @returns {string} Description of the advancement
   */
  getAdvancementDescription(currentStatus, targetStatus) {
    if (!this.isStatusAdvancement(currentStatus, targetStatus)) {
      return "Not an advancement";
    }

    const requiredPhases = this.getRequiredPhasesForStatus(targetStatus, currentStatus);

    if (requiredPhases.length === 0) {
      return `Advancing from ${currentStatus} to ${targetStatus}`;
    }

    const phaseNames = requiredPhases.map((phase) =>
      phase.replace(/^\d+-/, ""),
    );

    return `Advancing from ${currentStatus} to ${targetStatus} requires completion of: ${phaseNames.join(", ")}`;
  },
};
